import { useEffect, useState } from "react";
import { RING_TIER_DEFAULTS, type RingInvoice, type Tenant } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";
import { type RingStatus } from "../../components/RingSetup";

// The master admin's two Clevis Ring cards: a company's caps (on its page) and
// the pass-through rates every company sees (Pricing & Plans).

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const monthName = (key: string) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(Date.UTC(y ?? 2026, (m ?? 1) - 1, 1)).toLocaleString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
};

/** A company's Ring caps, usage, card and charges. Blank caps mean no limit. */
export function RingLimitsCard({ tenantId, tenant, onMessage }: { tenantId: string; tenant: Tenant; onMessage: (m: string) => void }) {
  const [numbers, setNumbers] = useState(tenant.ring?.numbers == null ? "" : String(tenant.ring.numbers));
  const [minutes, setMinutes] = useState(tenant.ring?.minutesPerMonth == null ? "" : String(tenant.ring.minutesPerMonth));
  const [hardStop, setHardStop] = useState(tenant.ring?.hardStop ?? false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<RingStatus | null>(null);
  const [billed, setBilled] = useState("");

  useEffect(() => {
    void call<RingStatus>("getRingStatus", { tenantId }, { mutation: false })
      .then(setStatus)
      .catch(() => setStatus(null));
  }, [tenantId]);

  const num = (v: string) => (v.trim() === "" ? null : Math.max(0, Math.round(Number(v))));

  async function save() {
    setBusy(true);
    try {
      await call("updateTenant", { tenantId, patch: { ring: { numbers: num(numbers), minutesPerMonth: num(minutes), hardStop } } });
      onMessage("Clevis Ring caps saved.");
    } catch (e) {
      onMessage((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function billNow() {
    setBusy(true);
    setBilled("");
    try {
      const r = await call<{ month: string; results: Array<{ invoice: RingInvoice | null }> }>("runRingBilling", { tenantId });
      const inv = r.results[0]?.invoice;
      setBilled(
        !inv
          ? `${monthName(r.month)}: Ring isn't on for this company.`
          : `${monthName(r.month)}: ${inv.numbers} number${inv.numbers === 1 ? "" : "s"}, ${inv.minutes} min, ${money(inv.amountCents)} · ${inv.status}${inv.error ? ` (${inv.error})` : ""}`,
      );
    } catch (e) {
      setBilled((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Clevis Ring</CardTitle>
        <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void save()}>
          Save
        </Button>
      </div>
      <p className="text-xs text-steel">Caps for this company; blank means no limit. The carrier charges pass through to their card either way.</p>
      {status ? (
        <p className="mt-1 text-xs text-ink">
          {status.agreed ? `Agreed ${new Date(status.agreed.at).toLocaleDateString()} by ${status.agreed.byEmail || "an admin"}` : "Not turned on yet"} ·{" "}
          {status.card ? (status.card.viaSubscription ? "charges ride on their subscription" : `${status.card.brand} ···${status.card.last4}`) : "no card on file"} · this month {status.usage.minutes.toLocaleString()} min,{" "}
          {status.numbersActive} number{status.numbersActive === 1 ? "" : "s"}, about {money(status.estimateCents)}
          {status.invoices[0] ? ` · last charge ${monthName(status.invoices[0].month)} ${money(status.invoices[0].amountCents)} (${status.invoices[0].status})` : ""}
        </p>
      ) : null}
      <div className="mt-2 grid gap-3 sm:grid-cols-2">
        <div>
          <Label>Numbers</Label>
          <Input type="number" min={0} value={numbers} placeholder="no limit" onChange={(e) => setNumbers(e.target.value)} />
        </div>
        <div>
          <Label>Minutes per month</Label>
          <Input type="number" min={0} value={minutes} placeholder="no limit" onChange={(e) => setMinutes(e.target.value)} />
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {Object.entries(RING_TIER_DEFAULTS).map(([tier, d]) => (
          <button
            key={tier}
            type="button"
            className="underline hover:text-ink"
            onClick={() => {
              setNumbers(String(d.numbers));
              setMinutes(String(d.minutesPerMonth));
            }}
          >
            {tier[0]!.toUpperCase() + tier.slice(1)} defaults ({d.numbers} / {d.minutesPerMonth.toLocaleString()})
          </button>
        ))}
        <button
          type="button"
          className="underline hover:text-ink"
          onClick={() => {
            setNumbers("");
            setMinutes("");
          }}
        >
          No limits
        </button>
      </div>
      <label className="mt-2 flex items-start gap-2 text-xs text-ink">
        <input type="checkbox" className="mt-0.5" checked={hardStop} onChange={(e) => setHardStop(e.target.checked)} />
        <span>Hard stop: at the minute cap, callers hear the line can't take calls until next month. Off (the default): calls keep going through, and owners, admins and Clevis get an email at 80% and 100%.</span>
      </label>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
        <Button type="button" variant="ghost" className="px-2 py-1 text-xs" busy={busy} onClick={() => void billNow()}>
          Bill last month now
        </Button>
        {billed ? <span className="text-ink">{billed}</span> : null}
      </div>
    </Card>
  );
}

/** The pass-through rates every company sees before agreeing. */
export function RingRatesCard() {
  const [dollarsPerNumber, setDollarsPerNumber] = useState("");
  const [centsPerMinute, setCentsPerMinute] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    void call<{ numberMonthlyCents: number; minuteCents: number }>("getRingRates", {}, { mutation: false })
      .then((r) => {
        setDollarsPerNumber((r.numberMonthlyCents / 100).toFixed(2));
        setCentsPerMinute(String(r.minuteCents));
      })
      .catch((e) => setNote((e as Error).message));
  }, []);

  async function save() {
    setBusy(true);
    setNote("");
    try {
      await call("setRingRates", { numberMonthlyCents: Math.round(Number(dollarsPerNumber) * 100), minuteCents: Math.round(Number(centsPerMinute)) });
      setNote("Saved. Companies that already agreed keep going at the new rates from the next bill.");
    } catch (e) {
      setNote((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <div className="flex items-center justify-between">
        <CardTitle className="mb-0">Clevis Ring pass-through rates</CardTitle>
        <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void save()}>
          Save
        </Button>
      </div>
      <p className="text-xs text-steel">
        What every company pays for phone numbers and forwarded minutes, charged to their card on the 1st. The carrier bills Clevis about $1.15 a number and about 2.3¢ a forwarded minute before taxes and fees.
      </p>
      <div className="mt-2 grid max-w-md gap-3 sm:grid-cols-2">
        <div>
          <Label>$ per number a month</Label>
          <Input type="number" min={0} step="0.01" value={dollarsPerNumber} onChange={(e) => setDollarsPerNumber(e.target.value)} />
        </div>
        <div>
          <Label>¢ per forwarded minute</Label>
          <Input type="number" min={0} step="1" value={centsPerMinute} onChange={(e) => setCentsPerMinute(e.target.value)} />
        </div>
      </div>
      {note ? <p className="mt-2 text-xs text-ink">{note}</p> : null}
    </Card>
  );
}
